import BULKS_SVC from '@efex-services/payables/bulksSvc';
import { Button } from '@efexteam/components';
import { useApiCall } from '@efexteam/use-api-call';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Collapse,
  Typography,
} from '@mui/material';
import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectProfileData } from '@/pages/profile/store/selectors';
import { iconExcel } from '../../assets';
import ExpandMore from '../common/ExpandMore';
import { SelectTemplateStyles } from './SelectTemplate.styles';
import useWhiteLabelService from '@/layout/login/hooks/useWhiteLabel.service';

const SelectTemplate = () => {
  const { i18n } = useTranslation();
  const { t } = useTranslation();
  const { company } = useSelector(selectProfileData) || {};
  const { enterpriseId } = company || {};
  const { whiteLabelState } = useWhiteLabelService();

  const [checkedCard, setCheckedCard] = useState<string | undefined>(undefined);
  const [expanded, setExpanded] = useState<boolean>(true);

  const { data, invoke: getBulksDocumentsList } = useApiCall(() => {
    if (!enterpriseId) return Promise.resolve({ availableBulks: [] });
    return BULKS_SVC.getBulksDocumentsList(enterpriseId);
  });

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const downloadBlob = useCallback((blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, []);

  const handleDownload = useCallback(async () => {
    if (!checkedCard || !enterpriseId) return;

    try {
      const response = await BULKS_SVC.getBulkDocumentByCountry(
        enterpriseId,
        checkedCard,
        i18n.language,
        whiteLabelState.organizationID ?? ''
      );

      if (response) {
        const filename = `template-bulks-${checkedCard}.xlsx`;
        downloadBlob(response, filename);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  }, [
    enterpriseId,
    checkedCard,
    i18n.language,
    whiteLabelState.organizationID,
    downloadBlob,
  ]);

  const handleCardSelection = useCallback((country: string) => {
    setCheckedCard(country);
  }, []);

  useEffect(() => {
    getBulksDocumentsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card className="px-2">
      <CardActions disableSpacing>
        <Typography variant="h6">{t('pyDownloadFileType')}</Typography>
        <ExpandMore
          expand={expanded}
          onClick={handleExpandClick}
          aria-expanded={expanded}
          aria-label="show more"
        >
          <ExpandMoreIcon />
        </ExpandMore>
      </CardActions>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent>
          <div className={SelectTemplateStyles.cardsContainer}>
            {data?.availableBulks?.map((item) => (
              <div key={item.country}>
                <Card
                  variant="outlined"
                  className={clsx(
                    'cursor-pointer flex flex-row p-3',
                    checkedCard === item.country ? 'isActiveCard' : ''
                  )}
                  onClick={() => handleCardSelection(item.country)}
                >
                  <Checkbox
                    inputProps={{ 'aria-label': item.country }}
                    checked={checkedCard === item.country}
                  />
                  <div>
                    <Typography>{item.country}</Typography>
                    <Typography variant="caption">
                      {t('templatesExcel')}
                      <img
                        src={iconExcel}
                        alt=""
                        className={SelectTemplateStyles.icon}
                      />
                    </Typography>
                  </div>
                </Card>
                {checkedCard === item.country && (
                  <Button
                    variant="text"
                    onClick={handleDownload}
                    appearance="primary"
                    className="!pl-0"
                  >
                    {t('pyDownload')}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default SelectTemplate;
